/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the drugShipment modal component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem, makeStyles, Theme, Typography, Box } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { DrugShipmentActions } from '../../../store/modules/drugmanagement/drugshipment';
import TextFieldWrapper from '../../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import NumberTextField from '../../../components/FinalForm/FinalFormControls/NumberTextField';
import DatePickerWrapper from '../../../components/FinalForm/FinalFormControls/DatePickerWrapper';
import { getDrugUnitName } from '../../../utilities/getDrugUnitName';
import { getShipmentStatusDateDescription } from '../../../utilities/getShipmentStatusText';
import useAsyncFunction from '../../../hooks/useAsyncFunction';
import drugManagementSettings from '../../../constants/drugManagementSettings';
import { CheckboxListWrapper } from '../../../components/FinalForm/FinalFormControls/CheckboxListWrapper';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    formControl: {
        width: '100%'
    },
    addDrugShipmentDepotBatchContainer: {
        textAlign: 'right'
    },
    remove: {
        color: theme.palette.error.main
    },
    pharmacistsList: {
        "& > div": {
            padding: '0.5rem',
            borderBottom: '1px solid rgba(0, 0, 0, 0.38)',
            width: '100%'
        }
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '2rem'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */



/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugShipmentModalProps {
    drugShipment: Dtos.DrugShipment | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    depotBatches: Dtos.DepotBatch[] | null;
    depotBatchesLoadState: IRequestState<Dtos.ResponseStatus>;
    pharmacists: Record<number, Dtos.Personnel> | null;
    pharmacistsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    pharmacyDrugs: Dtos.PharmacyDrug[] | null;
    institutions: Dtos.IInstitution[] | null;
    institutionLoadState: IRequestState<Dtos.ResponseStatus>;
    canAdministerDrugManagement: boolean;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IDrugShipmentFormProps extends FormRenderProps<Dtos.DrugShipment> {
    drugShipment: Dtos.DrugShipment | null;
    depotBatches: Dtos.DepotBatch[] | null;
    depotBatchesLoadState: IRequestState<Dtos.ResponseStatus>;
    pharmacists: Record<number, Dtos.Personnel> | null;
    pharmacistsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    pharmacyDrugs: Dtos.PharmacyDrug[] | null;
    institutions: Dtos.IInstitution[] | null;
    institutionLoadState: IRequestState<Dtos.ResponseStatus>;
    canAdministerDrugManagement: boolean;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IDrugShipmentForm extends Partial<Dtos.DrugShipment> {
}

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugUnitName = getDrugUnitName(true);

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

/**
 * This function validates that there's only one kind of depot batch id in the drug shipment.
 * It's been declared as a function rather than arrow function because an arrow function overwrites
 * the 'this' context, and updating yup to a higher version to get the TestContext as a variable in the function
 * breaks a lot of the validation.
 * @param this The test context
 * @param value The row being validated
 */
const drugValidator = function (this: Yup.TestContext,
    value: number) {
    const form: IDrugShipmentForm = this.options.context as unknown as IDrugShipmentForm;
    const batchArray: Dtos.DrugShipmentDepotBatch[] | undefined = form.drugShipmentDepotBatches;

    if (![Dtos.ShipmentStatus.Available, Dtos.ShipmentStatus.QuarantinedAtSite, Dtos.ShipmentStatus.Destroyed].find(x => x === form.shipmentStatus) &&
        batchArray &&
        batchArray.length > 0 &&
        value) {
        if (batchArray
            .filter(b => b.drugId === value)
            .length > 1) {
            return false;
        }
    }

    return true;
}

const depotValidator = function (this: Yup.TestContext,
    value: number) {
    const form: IDrugShipmentForm = this.options.context as unknown as IDrugShipmentForm;
    const batchArray: Dtos.DrugShipmentDepotBatch[] | undefined = form.drugShipmentDepotBatches;

    if ([Dtos.ShipmentStatus.Available, Dtos.ShipmentStatus.QuarantinedAtSite, Dtos.ShipmentStatus.Destroyed].find(x => x === form.shipmentStatus) &&
        batchArray &&
        batchArray.length > 0 &&
        value) {
        if (batchArray
            .filter(b => b.depotBatchId === value)
            .length > 1) {
            return false;
        }
    }

    return true;
}

const depotRequiredValidator = function (this: Yup.TestContext,
    value: number) {
    const form: IDrugShipmentForm = this.options.context as unknown as IDrugShipmentForm;

    if ([Dtos.ShipmentStatus.Available, Dtos.ShipmentStatus.QuarantinedAtSite, Dtos.ShipmentStatus.Destroyed].find(x => x === form.shipmentStatus) && !value) {
        return false
    }

    return true;
}

const drugUnitValidator = function (this: Yup.TestContext,
    value: number) {
    const drugShipmentDepotBatch = this.parent as unknown as Dtos.DrugShipmentDepotBatch;

    console.log(this)

    if (drugShipmentDepotBatch.drugId === Dtos.DrugTypes.AdjuvantNivolumab && value%5 > 0) {
        return false
    }

    return true;
}

const validation = Yup.object().shape<IDrugShipmentForm>({
    institutionCode: Yup.string().required(),
    shipmentStatus: Yup.number().required(),
    consignmentNo: Yup.string(),
    drugShipmentStatusHistory: Yup.array()
        .min(1, 'The shipment must have a date created')
        .of(Yup.object<Dtos.DrugShipmentStatusHistory>().shape({
            statusDate: Yup.string().required().label('Status Date')
        })),
    drugShipmentDepotBatches: Yup.array()
        .min(1, 'At least one batch of drugs must exist on the shipment')
        .of(Yup.object<Dtos.DrugShipmentDepotBatch>().shape({
            drugId: Yup.number().required().test('only-one',
                'You can only have one of each kind of drug',
                drugValidator)
                .label('Drug'),
            depotBatchId: Yup.number()
                .test('required', 'Batch is required', depotRequiredValidator)
                .test('only-one',
                'You can only have one of each kind of batch',
                depotValidator)
                .label('Batch'),
            drugUnits: Yup.number()
                .required()
                .min(0)
                .test('multiples', `${drugUnitName} must be multiple of 5`, drugUnitValidator)
                .label(drugUnitName)
        })),
    drugShipmentPharmacists: Yup.array()
        .min(1, 'Pharmacist must be selected')
        .max(1, 'Only one pharmacist must be selected')
        .of(Yup.object<Dtos.DrugShipmentPharmacist>())
});

const DrugShipmentForm: React.FunctionComponent<IDrugShipmentFormProps> = ({
    drugShipment,
    depotBatches,
    depotBatchesLoadState,
    pharmacists,
    pharmacistsLoadState,
    institutions,
    institutionLoadState,
    setModalOpen,
    canAdministerDrugManagement,
    onFormSave,
    pharmacyDrugs,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const classes = useStyles();

    const shipmentNotNew = !!drugShipment && drugShipment.shipmentStatus !== Dtos.ShipmentStatus.New;
    const shipmentExists = !!drugShipment && drugShipment.id;

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to save the drug shipment: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const institutionItems = React.useMemo(() => {
        if (!institutions || institutions.length === 0) {
            return [];
        }
        else {
            return institutions.map(institution => {
                return <MenuItem
                    key={institution.id}
                    value={institution.code}
                >
                    {institution.name}
                </MenuItem>;
            });
        }
    }, [institutions]);

    const drugItems = React.useMemo(() => {
        const drugs = pharmacyDrugs?.map(pd => pd.drug);

        const enabledDrugs = pharmacyDrugs?.filter(pd => pd.enabled)?.map(pd => pd.drug);

        return drugs?.map(drug => {
            return <MenuItem
                key={drug.id}
                value={drug.id}
                disabled={enabledDrugs?.find(x => x.id === drug.id) == null}
            >
                {`${drug.drugName}`}
            </MenuItem>;
        });
    }, [pharmacyDrugs]);

    const depotBatchItems = React.useCallback((index: number) => {
        var drugShipmentDepotBatches = formRenderProps.form.getFieldState(`drugShipmentDepotBatches`)?.value ?? [];

        var drugShipmentDepotBatch = drugShipmentDepotBatches.length > 0 ? drugShipmentDepotBatches[index] : null;

        var drugId = drugShipmentDepotBatch?.drugId;

        if (!depotBatches || depotBatches.length === 0) {
            return [];
        }
        else {
            const availableDepotBatches = depotBatches
                .filter(db =>
                    db.available &&
                    (db.drugUnitsAllocated + db.drugUnitsDestroyed < db.drugUnitsTotal) &&
                    DateTime.local().startOf('day') <= DateTime.fromISO(db.batch.allocationCutOffDate!))
                .reduce((accumulator, currentValue) => {
                    const existingValue = accumulator[currentValue.batchId];

                    if (!existingValue ||
                        existingValue.depotBatchNo! > currentValue.depotBatchNo!) {
                        accumulator[currentValue.batchId] = currentValue;
                    }

                    return accumulator;
                }, {} as Record<number, Dtos.DepotBatch>);

            const depotBatchesForForm = Object.keys(availableDepotBatches).map(key => availableDepotBatches[key]) as Dtos.DepotBatch[];

            const depotBatchMenuItems = depotBatches
                .filter(depotBatch => !drugId || depotBatch?.batch?.drugId === drugId)
                .map(depotBatch => {
                    return <MenuItem
                        key={depotBatch?.id}
                        value={depotBatch?.id}
                        disabled={depotBatchesForForm.find(x => x.id === depotBatch?.id) == null}
                    >
                        {`${depotBatch.batch.batchNo} (${depotBatch.depotBatchNo} Expires ${DateTime.fromISO(depotBatch.batch.dateExpiration!).toFormat('dd/MM/yyyy')})`}
                    </MenuItem>;
                });

            return depotBatchMenuItems;
        }
    }, [depotBatches, formRenderProps]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const batchFieldsDisabled = React.useMemo(() => {
        return formDisabled ||
            (shipmentNotNew && !canAdministerDrugManagement);
    }, [formDisabled, shipmentNotNew, canAdministerDrugManagement]);

    const remove = React.useCallback((index: number) => {
        formRenderProps.form.mutators.remove('drugShipmentDepotBatches', index);
    }, [formRenderProps.form.mutators]);

    const drugShipmentStatusHistory = React.useMemo(() => {
        if (drugShipment &&
            drugShipment.drugShipmentStatusHistory &&
            drugShipment.drugShipmentStatusHistory.length > 0) {
            return <FieldArray name="drugShipmentStatusHistory">
                {({ fields }) => {
                    return fields.map((field, index) => {
                        const shipmentStatus = drugShipment.drugShipmentStatusHistory[index].shipmentStatus;

                        return (
                            <React.Fragment key={index}>
                                <Grid item xs={12}>
                                    <Field
                                        fullWidth
                                        name={`${field}.statusDate`}
                                        component={DatePickerWrapper}
                                        label={getShipmentStatusDateDescription(shipmentStatus)}
                                        includeOffset={true}
                                        removeTime={true}
                                        disabled={formDisabled}
                                    />
                                </Grid>
                            </React.Fragment>
                        )
                    });
                }}
            </FieldArray>;
        }

        return null;
    }, [drugShipment, formDisabled]);

    const drugShipmentDepotBatches = React.useMemo(() => {
        const drugShipmentReceived = drugShipment?.shipmentStatus === Dtos.ShipmentStatus.Available || drugShipment?.shipmentStatus === Dtos.ShipmentStatus.QuarantinedAtSite || drugShipment?.shipmentStatus === Dtos.ShipmentStatus.Destroyed;

        if (drugShipment &&
            drugShipment.drugShipmentDepotBatches &&
            drugShipment.drugShipmentDepotBatches.length > 0) {
            return <FieldArray name="drugShipmentDepotBatches">
                {({ fields }) => {
                    return fields.map((field, index) => {
                        const onRemoveClick = () => {
                            remove(index);
                        };

                        return (
                            <React.Fragment key={index}>
                                <Grid item xs={drugShipmentReceived ? 4 : 8}>
                                    <Field
                                        name={`${field}.drugId`}
                                        component={SelectWrapper}
                                        type="text"
                                        label="Drug"
                                        formControlProps={{
                                            fullWidth: true
                                        }}
                                        disabled={batchFieldsDisabled}
                                    >
                                        {drugItems}
                                    </Field>
                                </Grid>
                                
                                {
                                    drugShipmentReceived && <Grid item xs={4}>
                                        {
                                            depotBatchesLoadState && depotBatchesLoadState.state && depotBatchesLoadState.state === RequestState.Pending ?
                                                <CircularProgress /> :
                                                <Field
                                                    name={`${field}.depotBatchId`}
                                                    component={SelectWrapper}
                                                    type="text"
                                                    label="Drug Batch"
                                                    formControlProps={{
                                                        fullWidth: true
                                                    }}
                                                    disabled={batchFieldsDisabled}
                                                >
                                                    {depotBatchItems(index)}
                                                </Field>
                                        }
                                    </Grid>
                                }
                                              
                                <Grid item xs={2}>
                                    <Field
                                        fullWidth
                                        name={`${field}.drugUnits`}
                                        component={NumberTextField}
                                        type="text"
                                        label={drugUnitName}
                                        disabled={batchFieldsDisabled}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    {
                                        !batchFieldsDisabled && (index > 0) ?
                                            <Button
                                                variant="text"
                                                color="default"
                                                className={classes.remove}
                                                onClick={onRemoveClick}
                                                disabled={batchFieldsDisabled}
                                            >
                                                <FontAwesomeIcon fixedWidth icon={faTimes} />
                                            </Button> :
                                            null
                                    }
                                </Grid>
                            </React.Fragment>
                        )
                    });
                }}
            </FieldArray>;
        }

        return null;
    }, [drugShipment, depotBatchesLoadState, depotBatchItems, batchFieldsDisabled, classes.remove, remove, drugItems]);

    const addDrugShipmentDepotBatchClick = React.useCallback(() => {
        formRenderProps.form.mutators.push('drugShipmentDepotBatches', new Dtos.DrugShipmentDepotBatch());
    }, [formRenderProps.form.mutators]);

    const showConsignmentNumber = drugManagementSettings.showConsignmentNumber &&
        (!drugManagementSettings.autofillConsignmentNumber || shipmentExists);

    const pharmacistsValues = React.useMemo(() => {
        if (!pharmacists || Object.keys(pharmacists).length === 0) {
            return [];
        }
        else {
            return Object.keys(pharmacists).map(key => {
                return {
                    label: <>
                        <div>{pharmacists[key]?.name}</div>
                        <small>{pharmacists[key]?.address}</small>
                    </>,
                    value: { pharmacistId: +key } as Dtos.DrugShipmentPharmacist
                } 
            });
        }
    }, [pharmacists]);

    const pharmacistsLoading = pharmacistsLoadState && pharmacistsLoadState.state && pharmacistsLoadState.state === RequestState.Pending;

    return (
        <>
            <DialogContent>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12}>
                        {
                            institutionLoadState && institutionLoadState.state && institutionLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="institutionCode"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={true}
                                    label="Institution"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {institutionItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            name="shipmentStatus"
                            component={SelectWrapper}
                            type="text"
                            disabled={true}
                            label="Status"
                            formControlProps={{
                                fullWidth: true
                            }}
                        >
                            <MenuItem value={Dtos.ShipmentStatus.New}>New</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.Ordered}>Ordered</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.InTransit}>In Transit</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.Available}>Available</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.Lost}>Lost</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.Destroyed}>Destroyed</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.QuarantinedFromTransit}>Quarantined From Transit</MenuItem>
                            <MenuItem value={Dtos.ShipmentStatus.QuarantinedAtSite}>Quarantined At Site</MenuItem>
                        </Field>
                    </Grid>
                    {
                         showConsignmentNumber ?
                            <Grid item xs={12}>
                                <Field
                                    fullWidth
                                    name="consignmentNo"
                                    component={TextFieldWrapper}
                                    type="text"
                                    label={drugManagementSettings.consignmentNumberLabel}
                                    disabled={formDisabled || drugManagementSettings.autofillConsignmentNumber}
                                />
                            </Grid> :
                            null
                    }
                    {
                        drugManagementSettings.showShipmentNumber &&
                        shipmentExists ?
                            <Grid item xs={12}>
                                <Field
                                    fullWidth
                                    name="pharmacyShipmentNo"
                                    component={TextFieldWrapper}
                                    type="text"
                                    label="Shipment Number"
                                    disabled={true}
                                />
                            </Grid> :
                            null
                    }
                    
                    {
                        drugShipmentStatusHistory
                    }
                    <Grid item xs={12}>
                        <Typography
                            variant="h4"
                        >
                            Pharmacists
                        </Typography>

                        <Box className={`${pharmacistsLoading ? classes.loadingContainer : classes.pharmacistsList}`}>
                        {
                            pharmacistsLoading ?
                                <CircularProgress /> :
                                <Field
                                    name="drugShipmentPharmacists"
                                    component={CheckboxListWrapper}
                                    values={pharmacistsValues}
                                    valueSelector={(drugShipmentPharmacist: Dtos.DrugShipmentPharmacist) => drugShipmentPharmacist.pharmacistId}
                                    xl={12}
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    fullWidth
                                />
                        }
                        </Box>
                    </Grid>
   
                    <Grid item xs={12}>
                        <Typography
                            variant="h4"
                        >
                            Drugs
                        </Typography>
                    </Grid>
                    {
                        drugShipmentDepotBatches
                    }


                    {
                        !batchFieldsDisabled ?
                            <Grid item xs={12} className={classes.addDrugShipmentDepotBatchContainer}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={addDrugShipmentDepotBatchClick}
                                    disabled={formDisabled}
                                >
                                    Add Batch to Shipment
                                </Button>
                            </Grid> :
                            null
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const DrugShipmentModal: React.FunctionComponent<IDrugShipmentModalProps> = ({
    drugShipment,
    modalOpen,
    setModalOpen,
    depotBatches,
    depotBatchesLoadState,
    pharmacists,
    pharmacistsLoadState,
    drugs,
    drugLoadState,
    institutions,
    institutionLoadState,
    canAdministerDrugManagement,
    onCloseClick,
    onFormSave,
    pharmacyDrugs
}) => {
    const drugShipmentForm = React.useCallback((props: FormRenderProps<Dtos.DrugShipment>) => {
        return (
            <DrugShipmentForm
                drugShipment={drugShipment}
                depotBatches={depotBatches}
                depotBatchesLoadState={depotBatchesLoadState}
                pharmacists={pharmacists}
                pharmacistsLoadState={pharmacistsLoadState}
                drugs={drugs}
                drugLoadState={drugLoadState}
                pharmacyDrugs={pharmacyDrugs}
                institutions={institutions}
                institutionLoadState={institutionLoadState}
                canAdministerDrugManagement={canAdministerDrugManagement}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [institutions, institutionLoadState, setModalOpen, drugShipment, depotBatches, depotBatchesLoadState, canAdministerDrugManagement, drugLoadState, drugs, onFormSave]);

    const asyncSave = useAsyncFunction(DrugShipmentActions.save, DrugShipmentActions.saveSuccess, DrugShipmentActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="md"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Drug Shipment
                </DialogTitle>
                <AsyncForm
                    initialValues={drugShipment}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={drugShipmentForm}
                    mutators={{
                        ...arrayMutators
                    }}
                />
            </Dialog>
        </>
    );
}

export default DrugShipmentModal;